/**
 * Lecteur du film des 40 ans, dans la banniere d'accueil.
 *
 * Au clic sur le disque, le contenu de la banniere s'efface en fondu et le lecteur prend
 * exactement le meme cadre. Faute de streaming adaptatif cote serveur, c'est le navigateur
 * qui choisit sa version : la legere sur petit ecran ou en economie de donnees, la large
 * sinon. Chaque langue pointe vers son propre fichier via les champs du gabarit.
 */

const ICON = {
  play: (s) => `<svg viewBox="0 0 24 24" width="${s}" height="${s}" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>`,
  pause: '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>',
  sound: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round" aria-hidden="true"><path d="M4 9v6h3.5L12 19V5L7.5 9H4z" fill="currentColor"/><path d="M16 9.2a3.6 3.6 0 0 1 0 5.6M18.6 6.6a7.2 7.2 0 0 1 0 10.8"/></svg>',
  muted: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round" aria-hidden="true"><path d="M4 9v6h3.5L12 19V5L7.5 9H4z" fill="currentColor"/><path d="M16.5 9.5l5 5m0-5l-5 5"/></svg>',
  expand: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/></svg>',
  collapse: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5"/></svg>',
  close: '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
};

const DEFAULT_LABELS = {
  play: 'Lire le film',
  pause: 'Pause',
  progress: 'Progression',
  mute: 'Couper le son',
  unmute: 'Rétablir le son',
  volume: 'Volume',
  fullscreen: 'Plein écran',
  exitFullscreen: 'Quitter le plein écran',
  close: 'Fermer',
};

const FADE = 450;

function readLabels(hero) {
  try {
    return Object.assign({}, DEFAULT_LABELS, JSON.parse(hero.dataset.filmLabels || '{}'));
  } catch (e) {
    return Object.assign({}, DEFAULT_LABELS);
  }
}

/**
 * La version legere sert les petits ecrans, l'economie de donnees et les reseaux lents.
 */
function pickSource(hero) {
  const large = hero.dataset.filmLarge || '';
  const light = hero.dataset.filmLight || '';
  const link = navigator.connection || {};
  const spare = window.innerWidth < 900
    || link.saveData === true
    || /(^|-)(2g|3g)$/.test(link.effectiveType || '');

  return (spare ? light : large) || large || light;
}

function formatTime(seconds) {
  const value = isFinite(seconds) && seconds > 0 ? seconds : 0;

  return `${Math.floor(value / 60)}:${String(Math.floor(value % 60)).padStart(2, '0')}`;
}

function buildPlayer(hero, labels) {
  const box = document.createElement('div');
  box.className = 'film-player';
  box.tabIndex = 0;
  box.innerHTML = `
    <video playsinline preload="auto"></video>
    <span class="film-player-veil"></span>
    <span class="film-player-spinner"></span>
    <button type="button" class="film-player-big" aria-label="${labels.play}">${ICON.play(30)}</button>
    <div class="film-player-bar">
      <div class="film-player-track" role="slider" aria-label="${labels.progress}"
           aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
        <span class="film-player-rail"><span class="film-player-buffer"></span><span class="film-player-played"></span></span>
        <span class="film-player-head"></span>
      </div>
      <div class="film-player-tools">
        <button type="button" class="film-player-toggle" aria-label="${labels.play}">${ICON.play(18)}</button>
        <span class="film-player-time">0:00 / 0:00</span>
        <span class="film-player-sound">
          <button type="button" class="film-player-mute" aria-label="${labels.mute}">${ICON.sound}</button>
          <span class="film-player-volume"><input type="range" min="0" max="1" step="0.02" value="1" aria-label="${labels.volume}"></span>
        </span>
        <button type="button" class="film-player-fullscreen" aria-label="${labels.fullscreen}">${ICON.expand}</button>
      </div>
    </div>`;

  const video = box.querySelector('video');
  const track = box.querySelector('.film-player-track');
  const played = box.querySelector('.film-player-played');
  const buffer = box.querySelector('.film-player-buffer');
  const head = box.querySelector('.film-player-head');
  const time = box.querySelector('.film-player-time');
  const toggle = box.querySelector('.film-player-toggle');
  const big = box.querySelector('.film-player-big');
  const mute = box.querySelector('.film-player-mute');
  const volume = box.querySelector('.film-player-volume input');
  const fullscreen = box.querySelector('.film-player-fullscreen');

  video.src = pickSource(hero);

  // Les commandes s'effacent pendant la lecture et reviennent au moindre mouvement.
  let timer = null;
  const reveal = () => {
    box.classList.add('is-visible');
    clearTimeout(timer);
    if (!video.paused) {
      timer = setTimeout(() => box.classList.remove('is-visible'), 2600);
    }
  };
  box.addEventListener('pointermove', reveal);
  box.addEventListener('pointerleave', () => {
    if (!video.paused) {
      box.classList.remove('is-visible');
    }
  });
  box.addEventListener('focusin', reveal);

  const start = () => {
    const promise = video.play();
    if (promise && promise.catch) {
      promise.catch(() => {});
    }
  };
  const alternate = () => (video.paused ? start() : video.pause());

  big.addEventListener('click', alternate);
  toggle.addEventListener('click', alternate);
  video.addEventListener('click', alternate);

  video.addEventListener('play', () => {
    box.classList.add('is-playing');
    toggle.innerHTML = ICON.pause;
    toggle.setAttribute('aria-label', labels.pause);
    reveal();
  });
  video.addEventListener('pause', () => {
    box.classList.remove('is-playing');
    toggle.innerHTML = ICON.play(18);
    toggle.setAttribute('aria-label', labels.play);
    clearTimeout(timer);
    box.classList.add('is-visible');
  });
  video.addEventListener('waiting', () => box.classList.add('is-waiting'));
  video.addEventListener('playing', () => box.classList.remove('is-waiting'));
  video.addEventListener('canplay', () => box.classList.remove('is-waiting'));
  video.addEventListener('ended', () => {
    box.classList.remove('is-playing');
    box.classList.add('is-visible');
  });

  const paint = () => {
    const total = video.duration || 0;
    const ratio = total ? (video.currentTime / total) * 100 : 0;
    played.style.width = `${ratio}%`;
    head.style.left = `${ratio}%`;
    track.setAttribute('aria-valuenow', Math.round(ratio));
    time.textContent = `${formatTime(video.currentTime)} / ${formatTime(total)}`;
  };
  video.addEventListener('timeupdate', paint);
  video.addEventListener('loadedmetadata', paint);
  video.addEventListener('progress', () => {
    if (!video.buffered.length || !video.duration) {
      return;
    }
    buffer.style.width = `${(video.buffered.end(video.buffered.length - 1) / video.duration) * 100}%`;
  });

  const aim = (event) => {
    const rect = track.getBoundingClientRect();
    const x = Math.min(Math.max(event.clientX - rect.left, 0), rect.width);
    if (video.duration) {
      video.currentTime = (x / rect.width) * video.duration;
    }
    paint();
  };
  track.addEventListener('pointerdown', (event) => {
    box.classList.add('is-scrubbing');
    try {
      track.setPointerCapture(event.pointerId);
    } catch (e) {
      // le navigateur refuse la capture, le glissement reste gere par les evenements
    }
    aim(event);
  });
  track.addEventListener('pointermove', (event) => {
    if (box.classList.contains('is-scrubbing')) {
      aim(event);
    }
  });
  const release = (event) => {
    if (!box.classList.contains('is-scrubbing')) {
      return;
    }
    box.classList.remove('is-scrubbing');
    try {
      track.releasePointerCapture(event.pointerId);
    } catch (e) {
      // rien a liberer
    }
  };
  track.addEventListener('pointerup', release);
  track.addEventListener('pointercancel', release);

  const refreshSound = () => {
    const silent = video.muted || video.volume === 0;
    mute.innerHTML = silent ? ICON.muted : ICON.sound;
    mute.setAttribute('aria-label', video.muted ? labels.unmute : labels.mute);
    volume.value = video.muted ? 0 : video.volume;
  };
  mute.addEventListener('click', () => {
    video.muted = !video.muted;
    refreshSound();
  });
  volume.addEventListener('input', () => {
    video.volume = parseFloat(volume.value);
    video.muted = video.volume === 0;
    refreshSound();
  });

  fullscreen.addEventListener('click', () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (box.requestFullscreen) {
      box.requestFullscreen();
    }
  });
  document.addEventListener('fullscreenchange', () => {
    const inside = document.fullscreenElement === box;
    fullscreen.innerHTML = inside ? ICON.collapse : ICON.expand;
    fullscreen.setAttribute('aria-label', inside ? labels.exitFullscreen : labels.fullscreen);
  });

  box.addEventListener('keydown', (event) => {
    if (event.target.tagName === 'INPUT') {
      return;
    }
    const action = {
      ' ': 'toggle', k: 'toggle', ArrowRight: 5, ArrowLeft: -5, m: 'mute', f: 'fullscreen',
    }[event.key];
    if (action === undefined) {
      return;
    }
    event.preventDefault();
    reveal();
    if (action === 'toggle') {
      alternate();
    } else if (action === 'mute') {
      video.muted = !video.muted;
      refreshSound();
    } else if (action === 'fullscreen') {
      fullscreen.click();
    } else {
      video.currentTime = Math.min(Math.max(video.currentTime + action, 0), video.duration || 0);
    }
  });

  refreshSound();
  box.classList.add('is-visible');

  // Le clic qui ouvre le lecteur vaut geste utilisateur : le son est donc autorise.
  requestAnimationFrame(() => {
    start();
    box.focus({ preventScroll: true });
  });

  return box;
}

function removePlayer(hero) {
  hero.classList.remove('is-playing');
  document.body.classList.remove('is-playing-film');
  hero.querySelectorAll('.film-player video').forEach((video) => video.pause());
  hero.querySelectorAll('.film-player, .film-close').forEach((node) => node.remove());
}

/**
 * Fermeture douce : on laisse le fondu se jouer avant de retirer les noeuds.
 */
function closePlayer(hero) {
  const nodes = hero.querySelectorAll('.film-player, .film-close');
  if (!nodes.length) {
    return;
  }
  nodes.forEach((node) => node.classList.remove('is-in'));
  hero.classList.remove('is-playing');
  hero.querySelectorAll('.film-player video').forEach((video) => video.pause());
  setTimeout(() => removePlayer(hero), FADE);
}

function openPlayer(hero, labels) {
  removePlayer(hero);

  const player = buildPlayer(hero, labels);
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'film-close';
  close.innerHTML = `${ICON.close}<span>${labels.close}</span>`;
  close.addEventListener('click', (event) => {
    event.stopPropagation();
    closePlayer(hero);
  });

  hero.appendChild(player);
  hero.appendChild(close);
  document.body.classList.add('is-playing-film');

  // Le fondu n'est lance qu'une fois la premiere image disponible, pour passer de la photo
  // au film plutot que de la photo a un rectangle noir. Au-dela de 700 ms on n'attend plus.
  const enter = () => {
    if (hero.classList.contains('is-playing')) {
      return;
    }
    hero.classList.add('is-playing');
    player.classList.add('is-in');
    close.classList.add('is-in');
  };

  const video = player.querySelector('video');
  if (video.readyState >= 2) {
    requestAnimationFrame(enter);
  } else {
    video.addEventListener('loadeddata', enter, { once: true });
    setTimeout(enter, 700);
  }
}

function setup(hero) {
  const button = hero.querySelector('.hero-film-play');
  if (!button || !pickSource(hero)) {
    return;
  }

  const labels = readLabels(hero);
  button.addEventListener('click', () => openPlayer(hero, labels));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && hero.classList.contains('is-playing')) {
      closePlayer(hero);
    }
  });
}

export default {
  init() {
    document.querySelectorAll('.hero-section.hero-film').forEach(setup);
  },
};



// WEBPACK FOOTER //
// ./scripts/components/hero-film.js